import { useState, type ReactNode } from 'react';
import Media from 'react-media';

import { SWS_HOST } from '@/constants';

import { useTheme } from '@simplywallst/ui-core';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { Button, Menu } from '@simplywallst/ui-core/next';
import DangerSolid from '@simplywallst/ui-core/icons/DangerSolid';
import EditSolid from '@simplywallst/ui-core/icons/EditSolid';
import BinSolid from '@simplywallst/ui-core/icons/BinSolid';
import { Badge, Tooltip } from '@simplywallst/ui-core/next';
import InformationSolid from '@simplywallst/ui-core/icons/InformationSolid';

import DocumentHeader from '@components/DocumentHeader';
import { NarrativeAuthor } from '@/components/NarrativeAuthor';

import type { Narrative } from '@/pages/Narrative/hooks/useNarrative';

import { NarrativeTimestamp } from './NarrativeTimestamp';
import { NarrativeActions } from './NarrativeActions';

import {
  getCompanyUrlSegments,
  getNarrativeUrl,
} from '../../utils/getNarrativeUrl';
import { NarrativeCommunityBreadcrumbs } from '../NarrativeCommunityBreadcrumbs';
import { CompanyNameWithIcon } from '../CompanyNameWithIcon';
import { ShareNarrative } from '../ShareNarrative';
import { FairValueHistoryGraph } from './components/FairValueHistoryGraph/FairValueHistoryGraph';
import { useUser } from '@/hooks/useUser';
import { getPathSafe } from '@/router/utils';
import { ROUTE_PUBLIC_NARRATIVE_EDIT } from '@/constants/routes';
import { usePathname } from '@/hooks/location/usePathname';
import { push } from 'redux-first-router';
import { useReportNarrative } from '@/pages/Narrative/hooks/useReportNarrative';
import { NarrativeDeleteConfirmation } from '@/components/NarrativeCard/components/NarrativeDeleteConfirmation';
import { useSnackbar } from '@/features/snackbar';
import { Image as BlurredBackground } from '@/pages/CompanyReport/components/Overview/components/Header/components/BlurredBackground';
import MoreHorizontalSolid from '@simplywallst/ui-core/icons/MoreHorizontalSolid';
import { useMe } from '@/components/User/components/Profile/components/CommunityProfile/hooks/useMe';

type Props = {
  narrative: Narrative;
  children?: ReactNode;
  onCommentClick: () => void;
};

export const NarrativeHeader = ({
  children,
  narrative,
  onCommentClick,
}: Props) => {
  const theme = useTheme();
  const { isMobile } = useUserAgent();
  const { data: user, queryStatus: useUserStatus } = useUser();
  const { data: me, status: useMeStatus } = useMe(
    {},
    { enabled: user?.loggedIn }
  );

  const {
    title,
    coverImageUrl,
    owner,
    company,
    userHoldsPosition,
    isDiscoverable,
    excerpt,
    isSponsored,
  } = narrative;

  const canonicalUrl = getNarrativeUrl({
    canonicalURL: company?.canonicalURL || '',
    narrativeSlug: narrative.slug,
  });

  const urlSegments = company?.canonicalURL
    ? getCompanyUrlSegments(company?.canonicalURL)
    : null;

  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);

  const { reportNarrativeWithTypeformDrawer } = useReportNarrative({
    narrativeId: narrative.id,
    canonicalUrl: `${SWS_HOST}${canonicalUrl}`,
    narrativeType: 'USER_DEFINED',
  });

  const isLoggedIn =
    useMeStatus === 'success' &&
    useUserStatus === 'success' &&
    user &&
    user.loggedIn;

  const isAuthor =
    isLoggedIn && owner.publicId === me?.me.communityProfile.publicId;

  const pathname = usePathname();

  const narrativeEditPath = getPathSafe({
    type: ROUTE_PUBLIC_NARRATIVE_EDIT,
    payload: {
      id: narrative.id,
      query: {
        returnPath: pathname,
      },
    },
  });

  const { addSnackbar, removeSnackbar } = useSnackbar();

  const dropDownOptions = [
    !isAuthor && {
      label: 'Report',
      value: 'report',
      icon: <DangerSolid className="size-5 fill-current" />,
      onClick: () => reportNarrativeWithTypeformDrawer(),
    },
    isAuthor && {
      label: 'Edit',
      value: 'edit',
      icon: <EditSolid className="size-5 fill-current" />,
      onClick: () => {
        if (!narrativeEditPath) {
          addSnackbar({
            id: 'NARRATIVE_EDIT_PATH_NOT_FOUND',
            type: 'negative',
            message: 'Narrative edit path not found',
            lifeTime: 5_000,
            actionLabel: 'Dismiss',
            actionCallback: () => {
              removeSnackbar({
                id: 'NARRATIVE_EDIT_PATH_NOT_FOUND',
              });
            },
          });
          return;
        }
        push(narrativeEditPath);
      },
    },
    isAuthor && {
      label: 'Delete',
      value: 'delete',
      icon: <BinSolid className="size-5 fill-current" />,
      onClick: () => setIsDeleteConfirmationOpen(true),
    },
  ].filter(Boolean) as {
    label: string;
    value: string;
    icon: JSX.Element;
    onClick: () => void;
  }[];

  return (
    <div data-testid="narrative-header">
      <DocumentHeader
        title={`${company?.tickerSymbol} - ${title}`}
        titleSuffix=""
        allowCrawlers={isDiscoverable}
        openGraphImageUrl={coverImageUrl}
        description={excerpt}
      >
        <link rel="canonical" href={`${SWS_HOST}${canonicalUrl}`} />
        <meta property="og:url" content={`${SWS_HOST}${canonicalUrl}`} />
      </DocumentHeader>
      <div className="relative overflow-hidden p-4 light:bg-p-white max-md:pb-x0 md:bg-[white]/[.01] lg:rounded-md lg:p-6 lg:shadow-lg">
        <BlurredBackground
          src={
            narrative.company?.media?.find((m) => m.transform === 'main-header')
              ?.url ?? ''
          }
          alt="logo"
        />
        <div className="grid gap-4">
          <div className="flex items-center justify-between">
            <NarrativeCommunityBreadcrumbs
              companyName={company?.name}
              companyNameSlug={urlSegments?.companyName}
              exchangeTicker={urlSegments?.exchangeTicker}
              market={urlSegments?.country}
              primaryIndustrySlug={urlSegments?.industry}
            />
            {dropDownOptions.length > 0 && (
              <Menu
                label={
                  <Button variant="subtle">
                    <MoreHorizontalSolid />
                  </Button>
                }
              >
                {dropDownOptions.map((option) => (
                  <Menu.Item key={option.value} onAction={option.onClick}>
                    {option.icon}
                    {option.label}
                  </Menu.Item>
                ))}
              </Menu>
            )}
          </div>
          <div className="grid min-h-[34px] grid-cols-[auto_min-content] items-center justify-start gap-x1">
            <CompanyNameWithIcon
              name={company?.name ?? ''}
              tickerSymbol={company?.tickerSymbol ?? ''}
              websiteUrl={company?.info?.url ?? ''}
              canonicalURL={company?.canonicalURL ?? ''}
            />
            {isSponsored && (
              <Tooltip
                placement="bottom"
                content={
                  <p className="m-0 w-x20 text-tiny text-solid">
                    {`This Narrative is sponsored by ${company?.name}. Simply Wall St received a fee to promote it. The views are those of the Sponsor and do not reflect Simply Wall St's opinions.`}
                  </p>
                }
              >
                <button>
                  <Badge className="grid grid-cols-[min-content_max-content] items-center justify-start gap-x0_25 bg-[--s-button-secondary-color-bg]">
                    <InformationSolid className="size-4 fill-chart-01" />
                    <span className="text-chart-01">Sponsored</span>
                  </Badge>
                </button>
              </Tooltip>
            )}
          </div>
          <h1 className="m-0 text-mega font-strong text-solid">{title}</h1>
          <Media
            queries={{ mobile: { maxWidth: theme.mdMaxRaw } }}
            defaultMatches={{ mobile: isMobile }}
          >
            {(matches) => {
              if (matches.mobile) {
                return (
                  <>
                    <div className="grid gap-4">
                      <div className="flex flex-col gap-4">
                        <NarrativeAuthor
                          owner={owner}
                          userHoldsPosition={userHoldsPosition}
                          isAiNarrative={narrative.type === 'AI'}
                          priceTargetAnalystCount={
                            company?.analysisValue?.priceTargetAnalystCount
                          }
                        />
                        <div className="flex gap-2">
                          <NarrativeTimestamp narrative={narrative} />
                          <ShareNarrative />
                        </div>
                      </div>
                      <div className="mt-1">
                        <NarrativeActions
                          narrative={narrative}
                          onCommentClick={onCommentClick}
                        />
                      </div>
                    </div>
                  </>
                );
              }
              return (
                <>
                  <div className="grid gap-4">
                    <div className="flex flex-col gap-4">
                      <div className="flex items-start gap-8">
                        <NarrativeAuthor
                          owner={owner}
                          userHoldsPosition={userHoldsPosition}
                          isAiNarrative={narrative.type === 'AI'}
                          priceTargetAnalystCount={
                            company?.analysisValue?.priceTargetAnalystCount
                          }
                        />
                        <div className="flex gap-2">
                          <NarrativeTimestamp narrative={narrative} />
                          <ShareNarrative />
                        </div>
                      </div>
                      <div className="mt-1">
                        <NarrativeActions
                          narrative={narrative}
                          onCommentClick={onCommentClick}
                        />
                      </div>
                    </div>
                  </div>
                </>
              );
            }}
          </Media>
          {children}
        </div>
        {company?.id && (
          <div className="mt-4">
            <FairValueHistoryGraph narrative={narrative} />
          </div>
        )}
        {isDeleteConfirmationOpen && (
          <NarrativeDeleteConfirmation
            narrativeId={narrative.id}
            isDeleteConfirmationOpen={isDeleteConfirmationOpen}
            setIsDeleteConfirmationOpen={setIsDeleteConfirmationOpen}
          />
        )}
      </div>
    </div>
  );
};
